import { baseApi } from '../baseApi';
import { createAuthTokenError } from '../authTokenError';
import { getAccessToken, acquireGraphToken } from '@/services/token';
import { telemetry } from '@/services/telemetry';
import { env } from '@/utils/env';
import { API_PATHS } from '@/constants';
import { getContentApiImageUrl } from '@/utils/contentApi';
import type {
  EmployeePhotoResult,
  ContentImageResult,
  GraphProfilePhotoResult,
  EmployeePhotoResponse,
} from '@store/models';

const GRAPH_PHOTO_URL = 'https://graph.microsoft.com/v1.0/me/photo/$value';

function isEmployeePhotoResponse(value: unknown): value is EmployeePhotoResponse {
  return (
    typeof value === 'object' && value !== null && 'img' in value && typeof value.img === 'string'
  );
}

function isAbortError(error: unknown, signal?: AbortSignal): boolean {
  return signal?.aborted === true || (error instanceof DOMException && error.name === 'AbortError');
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read blob'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Media reads (feature 107): employee photo, authenticated content image, and the
 * Microsoft Graph profile photo. All previously lived in ad-hoc `fetch()` calls inside
 * hooks/services; they now flow through the store's API layer so every server call is
 * store-owned (FR-001, FR-002). Each endpoint resolves to a serializable `dataUrl` (or
 * `null` on a resolved miss), which RTK Query caches per argument — replacing the bespoke
 * module-scoped photo caches.
 */
export const mediaServiceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Employee profile photo via APIM. Resolves to a base64 data URL, or `null` on miss.
    getEmployeePhoto: builder.query<EmployeePhotoResult, string>({
      queryFn: async (employeeId, { signal }) => {
        try {
          const token = await getAccessToken();
          if (!token) {
            // Not a resolved miss: don't cache a missing-token failure for the whole session.
            return { error: createAuthTokenError() };
          }
          const url = `${env().apiBaseUrl}${API_PATHS.EMPLOYEE_PHOTO}/${encodeURIComponent(
            employeeId
          )}/photo`;
          const response = await fetch(url, {
            headers: { Authorization: `Bearer ${token}` },
            signal,
          });

          if (!response.ok) {
            // 4xx/5xx: resolved miss — cached so the session does not re-request.
            return { data: { dataUrl: null } };
          }

          const data: unknown = await response.json();
          if (isEmployeePhotoResponse(data) && data.img) {
            return { data: { dataUrl: `data:image/jpeg;base64,${data.img}` } };
          }
          return { data: { dataUrl: null } };
        } catch (error) {
          if (isAbortError(error, signal)) {
            return { error: { status: 'FETCH_ERROR', error: 'aborted' } };
          }
          // Transient network/parse failure: resolved miss, falls back to initials.
          return { data: { dataUrl: null } };
        }
      },
    }),

    // Authenticated content image (blob) converted to a base64 data URL.
    getContentImage: builder.query<ContentImageResult, string>({
      queryFn: async (blobName, { signal }) => {
        try {
          const token = await getAccessToken();
          if (!token) {
            return { error: createAuthTokenError() };
          }
          const url = getContentApiImageUrl(blobName);
          const response = await fetch(url, {
            headers: { Authorization: `Bearer ${token}` },
            signal,
          });

          if (!response.ok) {
            return { error: { status: response.status, data: undefined } };
          }

          const blob = await response.blob();
          const dataUrl = await blobToDataUrl(blob);
          return { data: { dataUrl } };
        } catch (error) {
          if (isAbortError(error, signal)) {
            return { error: { status: 'FETCH_ERROR', error: 'aborted' } };
          }
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
    }),

    // Microsoft Graph profile photo for the active account (separate MSAL scope).
    getGraphProfilePhoto: builder.query<GraphProfilePhotoResult, void>({
      queryFn: async (_arg, { signal }) => {
        try {
          const token = await acquireGraphToken();
          if (!token) {
            return { data: { dataUrl: null } };
          }

          const response = await fetch(GRAPH_PHOTO_URL, {
            headers: { Authorization: `Bearer ${token}` },
            signal,
          });

          if (!response.ok) {
            if (response.status !== 404) {
              telemetry.trackFailure('ProfilePhoto', 'FetchPhoto', `HTTP ${response.status}`, {
                statusCode: String(response.status),
              });
            }
            return { data: { dataUrl: null } };
          }

          const blob = await response.blob();
          const dataUrl = await blobToDataUrl(blob);
          return { data: { dataUrl } };
        } catch (error) {
          if (isAbortError(error, signal)) {
            return { error: { status: 'FETCH_ERROR', error: 'aborted' } };
          }
          const message = error instanceof Error ? error.message : 'Unknown error';
          telemetry.trackFailure('ProfilePhoto', 'FetchPhoto', message);
          return { data: { dataUrl: null } };
        }
      },
    }),
  }),
});

export const { useGetEmployeePhotoQuery, useGetContentImageQuery, useGetGraphProfilePhotoQuery } =
  mediaServiceApi;
